<template>
  <Teleport to="body">
    <!-- No backdrop: only the card and the strip take the pointer, never this layer. -->
    <div v-if="cardProps" class="pointer-events-none fixed inset-0 z-30">
      <!-- One box for the pair; its placement is latched (see `applyPlacement`). -->
      <div :class="[PAIR, POSITION[rendered]]">
        <Transition
          name="stop"
          mode="out-in"
          appear
          :duration="MOTION_REGULAR_MS"
          @before-leave="startLeave"
          @after-leave="finishLeave"
          @leave-cancelled="leaving = false"
        >
          <!-- Placement rides the key, so moving the card replays the pair. -->
          <JourneyCard
            v-if="landed && !leaving"
            :key="`${journeyStep}-${placement}`"
            v-bind="cardProps"
            :focus-cta="focusHeldBy === 'card'"
            @next="handleNext"
            @back="handleBack"
            @close="handleClose"
          />
        </Transition>

        <!-- As wide as its tabs, capped by the card. Never unmounted and never
                     out of the layout — only its opacity moves. -->
        <JourneyStrip
          ref="strip"
          :data-hidden="!stripVisible || undefined"
          :inert="!stripVisible"
          :stops="stripStops"
          :current="journeyStep ?? 0"
          class="max-w-full justify-self-center"
          @select="handleSelect"
        />
      </div>

      <!-- The camera measures this: on a pinned stop the real card is unmounted
                 for the whole flight, and the aim needs the box it will land in. -->
      <div
        v-if="placement === 'bottom'"
        :class="[PAIR, DOCK]"
        data-journey-twin
        aria-hidden="true"
        inert
      >
        <!-- Same object as the real card, or the boxes stop matching. -->
        <JourneyCard :data-journey-dock="journeyStep" v-bind="cardProps" />
      </div>

      <!-- Never remounted: a live region torn down with its content is one
                 screen readers miss. -->
      <p class="sr-only" aria-live="polite">
        {{ progress ? i18n(translations.stepProgress, progress) : '' }}
      </p>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { onKeyStroke } from '@vueuse/core'

import { useIsPhone } from '~/scopes/campaign/composables/useIsPhone'
import { useJourneyCamera } from '~/scopes/campaign/composables/useJourneyCamera'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { MOTION_REGULAR_MS } from '~/scopes/campaign/const/config.const'
import {
  journeyFor,
  stopAt,
  stopToken,
} from '~/scopes/campaign/data/journeys/data'

import JourneyCard from './JourneyCard.vue'
import translations from './JourneyOverlay.translations'
import JourneyStrip from './JourneyStrip.vue'
import type { JourneyStripStop } from './JourneyStrip.vue'

/** Where the pair sits: under the marker, dead centre, or above the phone's footer row. */
type JourneyPlacement = 'below' | 'center' | 'bottom'

// `minmax(0,1fr)`, not the default `auto`: an auto track would let the strip's overflow
// widen the whole pair instead of scrolling inside it.
const PAIR =
  'absolute grid w-[min(460px,calc(100%-var(--raw-edge-space)*2))] grid-cols-[minmax(0,1fr)] gap-[--raw-journey-pair-gap]'

// `center` fills the chrome's band and centres the rows: `h-fit` between pinned edges is
// a full-band box on WebKit, which stretches the card (`.claude/rules/guided-mining.md`).
const POSITION: Record<JourneyPlacement, string> = {
  below:
    'left-[50%] translate-x-[-50%] top-[calc(var(--raw-journey-marker-y)*100%+var(--raw-journey-card-offset))]',
  center:
    'bottom-[--raw-footer-offset] left-[50%] top-[--raw-header-offset] translate-x-[-50%] content-center',
  bottom: 'bottom-[--raw-footer-offset] left-[50%] translate-x-[-50%]',
}

// The twin holds the card alone, so keep this in step with `POSITION.bottom` plus the
// strip's row.
const DOCK =
  'bottom-[calc(var(--raw-footer-offset)+var(--raw-journey-nav-height)+var(--raw-journey-pair-gap))] left-[50%] translate-x-[-50%]'

const { trackClick } = useTracking()
const {
  activeJourney,
  activeView,
  journeyStep,
  goToStop,
  journeyBack,
  exitJourney,
  finishJourney,
} = useModalState()

const { landedStop } = useJourneyCamera()
const isPhone = useIsPhone()
const i18n = useI18n()
const strip = useTemplateRef('strip')

// `useIsPhone` is false through SSR, so a deep link would otherwise bake in desktop.
const mounted = ref(false)

const journey = computed(() => journeyFor(activeJourney.value))
const total = computed(() => journey.value?.stops.length ?? 0)

// A broken URL is repaired by the route's `beforeEnter`, so there is no fallback here.
const stop = computed(() =>
  journey.value && journeyStep.value
    ? stopAt(journey.value, journeyStep.value)
    : null,
)

// A pin, not just a camera move: it is what the card sits under and waits for.
const hasMarker = computed(() => Boolean(stop.value?.location?.pin))

// "The camera is settled on my stop", never "a flight is running"; `null` is a cold
// entry, which has nothing to wait for.
const landed = computed(
  () =>
    !hasMarker.value ||
    landedStop.value === null ||
    landedStop.value === journeyStep.value,
)

// Bound to the real card and to the camera's twin alike. `parts` is the only prop the stop
// doesn't author: it is the journey metal's, which the card has no business knowing about.
const cardProps = computed(() => {
  const current = stop.value
  const step = journeyStep.value
  if (!current || !step) return null

  return {
    title: current.title,
    place: current.place,
    body: current.body,
    citations: current.citations,
    parts:
      step === 1 ? journey.value?.metal.content?.smartphone?.parts : undefined,
    bodyAfter: current.bodyAfter,
    cta: current.cta,
    step,
  }
})

// A phone docks every stop: centred, the opening card fills the band it sits in.
const placement = computed<JourneyPlacement>(() => {
  if (mounted.value && isPhone.value) return 'bottom'

  return hasMarker.value ? 'below' : 'center'
})

// A tab per stop that authored a label — every one of them today, opening beat included.
// The index is the 1-based step it navigates to; no label is how a stop opts out.
const stripStops = computed<JourneyStripStop[]>(() =>
  (journey.value?.stops ?? []).flatMap((entry, index) =>
    entry.label
      ? [{ step: index + 1, slug: entry.slug, label: entry.label }]
      : [],
  ),
)

// Announced, not shown. It counts what the strip lists, so the two agree.
const progress = computed(() => {
  const step = journeyStep.value
  if (!step) return null

  return { current: step, total: total.value }
})

// What the box is wearing: taking `placement` straight would move it while the outgoing
// card is still fading, and the card would teleport before it goes.
const rendered = ref<JourneyPlacement>(placement.value)

// A card is mid-fade. `mode="out-in"` only defers an enter that replaces a child, and a
// pinned stop unmounts its card for the flight instead — so the gate has to.
const leaving = ref(false)

// Who held the keyboard when the fade began; every step remounts the card and drops focus
// on the body, after which Enter stops advancing.
const focusHeldBy = ref<'card' | 'strip' | null>(null)

// Only whether it is seen: it goes through a flight and through a placement change, so it
// comes back with the card rather than ahead of it.
const stripVisible = computed(
  () => landed.value && rendered.value === placement.value,
)

function applyPlacement() {
  rendered.value = placement.value
}

function startLeave(el: Element) {
  leaving.value = true
  if (el.contains(document.activeElement)) focusHeldBy.value = 'card'
  else focusHeldBy.value = strip.value?.holdsFocus() ? 'strip' : null
}

// Both in one tick, so the box re-places and the next card mounts in a single render.
function finishLeave() {
  leaving.value = false
  applyPlacement()
}

// A placement can also change with no fade running — a rotate, or the journey opening.
// `post` puts this after `before-leave`, which runs inside the patch.
watch(
  placement,
  () => {
    if (!leaving.value) applyPlacement()
  },
  { flush: 'post' },
)

// Exiting mid-fade takes the layer with no `after-leave`, and a stuck flag would leave
// the next entry with no card at all.
watch(stop, (current) => {
  if (!current) leaving.value = false
})

// The strip is never unmounted, but `inert` drops focus as surely as a remount does.
watch(stripVisible, (visible) => {
  if (visible && focusHeldBy.value === 'strip') strip.value?.focusActive()
})

function track(name: string) {
  trackClick({
    zone: 'journey',
    name: `${activeJourney.value}-${name}`,
    campaign: 'raw-materials',
  })
}

// The last stop is the one that finishes — nothing marks it.
function handleNext() {
  const step = journeyStep.value
  if (!step) return
  if (step === total.value) {
    track('finish')
    finishJourney()
  } else {
    track('next')
    goToStop(step + 1)
  }
}

function handleBack() {
  track('back')
  journeyBack()
}

function handleClose() {
  track('exit')
  exitJourney()
}

// Named, not numbered — the same token the URL and the page view carry.
function handleSelect(step: number) {
  const current = journey.value
  if (!current) return
  track(`stop-${stopToken(current, step)}`)
  goToStop(step)
}

// Escape goes to the topmost layer: a footer document can be open over the card.
onKeyStroke('Escape', (event) => {
  if (!stop.value || activeView.value) return
  event.preventDefault()
  handleClose()
})
</script>
